import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { COVERS } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Upload, Plus } from "lucide-react";

const CITY_COORDS = {
  madrid: [40.4168, -3.7038], barcelona: [41.3874, 2.1686], valencia: [39.4699, -0.3763],
  sevilla: [37.3891, -5.9845], berlin: [52.52, 13.405], london: [51.5074, -0.1278],
  paris: [48.8566, 2.3522], "new york": [40.7128, -74.006],
};

export default function CreateStudioDialog({ open, onClose, onCreated, editStudio }) {
  const { t } = useLang();
  const [f, setF] = useState({
    name: "", type: "recording", description: "", price_per_hour: "", city: "", address: "",
    open_hour: 9, close_hour: 22, engineer_available: false, engineer_price_per_hour: "",
    photo: COVERS[0].url, amenities: "", engineer_bio: "", engineer_photo: "", photos: [COVERS[0].url], contact_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const handleAddPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, photos: [...prev.photos, `${process.env.REACT_APP_BACKEND_URL}${data.url}`] }));
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, [key]: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (open && editStudio) {
      setF({
        name: editStudio.name || "", type: editStudio.type || "recording", description: editStudio.description || "",
        price_per_hour: String(editStudio.price_per_hour ?? ""), city: editStudio.city || "", address: editStudio.address || "",
        open_hour: editStudio.open_hour ?? 9, close_hour: editStudio.close_hour ?? 22,
        engineer_available: !!editStudio.engineer_available, engineer_price_per_hour: String(editStudio.engineer_price_per_hour ?? ""),
        photo: (editStudio.photos && editStudio.photos[0]) || COVERS[0].url,
        amenities: (editStudio.amenities || []).join(", "), engineer_bio: editStudio.engineer_bio || "", engineer_photo: editStudio.engineer_photo || "",
        photos: (editStudio.photos && editStudio.photos.length) ? editStudio.photos : [COVERS[0].url],
        contact_phone: editStudio.contact_phone || "",
      });
    } else if (open && !editStudio) {
      setF({
        name: "", type: "recording", description: "", price_per_hour: "", city: "", address: "",
        open_hour: 9, close_hour: 22, engineer_available: false, engineer_price_per_hour: "",
        photo: COVERS[0].url, amenities: "", engineer_bio: "", engineer_photo: "", photos: [COVERS[0].url], contact_phone: "",
      });
    }
  }, [open, editStudio]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const coord = CITY_COORDS[f.city.trim().toLowerCase()] || [40.4168 + (Math.random() - 0.5) * 0.1, -3.7038 + (Math.random() - 0.5) * 0.1];
      const payload = {
        name: f.name, type: f.type, description: f.description,
        price_per_hour: parseFloat(f.price_per_hour) || 0, currency: "EUR",
        city: f.city, address: f.address, lat: coord[0], lng: coord[1],
        photos: f.photos.length ? f.photos : [COVERS[0].url], amenities: f.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        open_hour: parseInt(f.open_hour) || 9, close_hour: parseInt(f.close_hour) || 22,
        engineer_available: f.engineer_available,
        engineer_price_per_hour: f.engineer_available ? parseFloat(f.engineer_price_per_hour) || 0 : 0,
        engineer_bio: f.engineer_available ? f.engineer_bio : "",
        engineer_photo: f.engineer_available ? f.engineer_photo : "",
        contact_phone: f.contact_phone,
      };
      if (editStudio) {
        await api.put(`/studios/${editStudio.id}`, payload);
        toast.success(t("listing_updated"));
      } else {
        await api.post("/studios", payload);
        toast.success(t("listing_created"));
      }
      onClose();
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="create-studio-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editStudio ? t("edit_studio") : t("new_studio")}</DialogTitle><DialogDescription className="sr-only">{t("submit_listing")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">{t("studio_name")}</Label>
            <Input data-testid="new-studio-name" required value={f.name} onChange={upd("name")} className="mt-1 bg-secondary/50" />
          </div>

          <div>
            <Label className="text-xs mb-1 block">{t("type")}</Label>
            <div className="grid grid-cols-2 gap-2">
              {["recording", "rehearsal"].map((tp) => (
                <button key={tp} type="button" data-testid={`new-type-${tp}`} onClick={() => setF({ ...f, type: tp })}
                  className={`h-10 rounded-xl border text-sm font-medium ${f.type === tp ? "border-primary bg-primary/15" : "border-border bg-secondary/40 text-muted-foreground"}`}>
                  {t(tp)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">{t("description")}</Label>
            <Textarea data-testid="new-studio-desc" value={f.description} onChange={upd("description")} className="mt-1 bg-secondary/50" rows={2} />
          </div>

          <div>
            <Label className="text-xs">{t("equipment")}</Label>
            <Textarea data-testid="new-studio-equipment" value={f.amenities} onChange={upd("amenities")} className="mt-1 bg-secondary/50" rows={2} placeholder={t("equipment_hint")} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">{t("city")}</Label><Input data-testid="new-studio-city" required value={f.city} onChange={upd("city")} className="mt-1 bg-secondary/50" /></div>
            <div><Label className="text-xs">{t("price_hour")}</Label><Input data-testid="new-studio-price" required type="number" min="1" value={f.price_per_hour} onChange={upd("price_per_hour")} className="mt-1 bg-secondary/50" /></div>
          </div>
          <div><Label className="text-xs">{t("address")}</Label><Input data-testid="new-studio-address" value={f.address} onChange={upd("address")} className="mt-1 bg-secondary/50" /></div>
          <div><Label className="text-xs">{t("contact_phone")}</Label><Input data-testid="new-studio-contact-phone" value={f.contact_phone} onChange={upd("contact_phone")} className="mt-1 bg-secondary/50" placeholder="+34 600 000 000" /><p className="text-[11px] text-muted-foreground mt-1">{t("contact_phone_hint")}</p></div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">{t("open_hour")}</Label><Input type="number" min="0" max="23" value={f.open_hour} onChange={upd("open_hour")} className="mt-1 bg-secondary/50" /></div>
            <div><Label className="text-xs">{t("close_hour")}</Label><Input type="number" min="1" max="24" value={f.close_hour} onChange={upd("close_hour")} className="mt-1 bg-secondary/50" /></div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5">
            <Label className="text-sm">{t("offer_engineer")}</Label>
            <Switch data-testid="new-engineer-toggle" checked={f.engineer_available} onCheckedChange={(v) => setF({ ...f, engineer_available: v })} />
          </div>
          {f.engineer_available && (
            <div className="space-y-3">
              <div><Label className="text-xs">{t("engineer_rate")}</Label><Input data-testid="new-engineer-rate" type="number" min="0" value={f.engineer_price_per_hour} onChange={upd("engineer_price_per_hour")} className="mt-1 bg-secondary/50" /></div>
              <div><Label className="text-xs">{t("engineer_bio")}</Label><Textarea data-testid="new-engineer-bio" value={f.engineer_bio} onChange={upd("engineer_bio")} rows={2} placeholder={t("engineer_bio_hint")} className="mt-1 bg-secondary/50" /></div>
              <div>
                <Label className="text-xs mb-1 block">{t("engineer_photo")}</Label>
                <div className="flex items-center gap-3">
                  {f.engineer_photo && <img src={f.engineer_photo} alt="eng" className="w-14 h-14 rounded-lg object-cover border border-border" />}
                  <label data-testid="upload-engineer-label" className="flex items-center gap-2 h-10 px-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-sm font-medium cursor-pointer hover:bg-emerald-500/20 transition-colors">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t("upload_photo")}
                    <input data-testid="upload-engineer-input" type="file" accept="image/*" onChange={handleUpload("engineer_photo")} className="hidden" />
                  </label>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs mb-1 block">{t("photos")}</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {f.photos.map((p, i) => (
                <div key={i} className="relative w-20 h-14">
                  <img src={p} alt="" className="w-full h-full object-cover rounded-lg border border-border" />
                  {f.photos.length > 1 && (
                    <button type="button" data-testid={`remove-photo-${i}`} onClick={() => setF((prev) => ({ ...prev, photos: prev.photos.filter((_, j) => j !== i) }))}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs leading-none">×</button>
                  )}
                </div>
              ))}
              <label data-testid="upload-photo-label" className="w-20 h-14 rounded-lg border border-dashed border-primary/50 bg-primary/10 flex items-center justify-center cursor-pointer hover:bg-primary/20 transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                <input data-testid="upload-photo-input" type="file" accept="image/*" onChange={handleAddPhoto} className="hidden" />
              </label>
            </div>
            <p className="text-xs text-muted-foreground mb-2">{t("or_choose_preset")}</p>
            <div className="grid grid-cols-3 gap-2">
              {COVERS.map((c) => (
                <button key={c.url} type="button" data-testid={`cover-${c.label}`}
                  onClick={() => setF((prev) => ({ ...prev, photos: prev.photos.includes(c.url) ? prev.photos.filter((x) => x !== c.url) : [...prev.photos, c.url] }))}
                  className={`h-16 rounded-lg overflow-hidden border-2 ${f.photos.includes(c.url) ? "border-primary" : "border-transparent"}`}>
                  <img src={c.url} alt={c.label} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <Button data-testid="submit-studio-btn" type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editStudio ? t("save_changes") : t("submit_listing"))}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
